import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Download, Mail, Phone, Trash2, Eye, Search, ArrowUpDown, FileText } from 'lucide-react';
import { generateGuestListPDF } from '@/utils/pdfGenerator';
import { useAuth } from '@/contexts/AuthContext';
import QRCode from 'qrcode';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestListManager({ eventId }) {
  const [guests, setGuests] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showTicket, setShowTicket] = useState(false);
  const [guestTicket, setGuestTicket] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [eventData, setEventData] = useState(null);
  const { tenant } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_title: '',
    ticket_type_id: '',
    payment_status: 'free',
    notes: ''
  });
  const [selectedGuests, setSelectedGuests] = useState([]);

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      const [guestsRes, ticketTypesRes, eventRes] = await Promise.all([
        axios.get(`${API_URL}/events/${eventId}/guests`),
        axios.get(`${API_URL}/events/${eventId}/ticket-types`),
        axios.get(`${API_URL}/events/${eventId}`)
      ]);
      setGuests(guestsRes.data);
      setTicketTypes(ticketTypesRes.data);
      setEventData(eventRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/events/${eventId}/guests`, formData);
      toast.success('Ospite aggiunto con biglietto gratuito!');
      setShowForm(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        ticket_type_id: '',
        payment_status: 'free',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante aggiunta');
    }
  };

  const resendTicket = async (guestId) => {
    try {
      await axios.post(`${API_URL}/guests/${guestId}/resend-ticket`);
      toast.success('📧 Biglietto reinviato via email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante reinvio biglietto');
    }
  };

  const handleDelete = async (guestId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo ospite?')) return;
    try {
      await axios.delete(`${API_URL}/guests/${guestId}`);
      toast.success('Ospite eliminato');
      fetchData();
    } catch (error) {
      toast.error('Errore durante eliminazione');
    }
  };

  const viewTicket = async (guestId) => {
    try {
      const response = await axios.get(`${API_URL}/guests/${guestId}/ticket`);
      const ticketData = response.data;
      setGuestTicket(ticketData);
      
      // Generate QR code as data URL
      if (ticketData.qr_code) {
        const qrDataUrl = await QRCode.toDataURL(ticketData.qr_code, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });
        setQrCodeUrl(qrDataUrl);
      }
      
      setShowTicket(true);
    } catch (error) {
      toast.error('Errore nel caricamento biglietto');
    }
  };

  const exportContacts = () => {
    const csv = [
      ['Nome', 'Cognome', 'Email', 'Telefono', 'Qualifica', 'Tipologia Biglietto', 'Stato Pagamento', 'Note'],
      ...guests.map(g => [
        g.first_name,
        g.last_name,
        g.email,
        g.phone,
        g.job_title || '-',
        g.ticket_type?.name || '-',
        g.payment_status === 'paid' ? 'Pagato' : g.payment_status === 'pending' ? 'In Attesa' : 'Gratuito',
        g.notes || '-'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guest-list-${eventId}-${Date.now()}.csv`;
    a.click();
    toast.success('Database contatti esportato!');
  };

  const printBadge = async (guestId) => {
    try {
      const response = await axios.post(
        `${API_URL}/events/${eventId}/guests/${guestId}/badge`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `badge_${guestId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Badge generato con successo!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Generazione badge disponibile solo nei piani Full ed Enterprise');
      } else {
        toast.error('Errore nella generazione del badge');
      }
    }
  };

  const printBadgesBatch = async () => {
    if (selectedGuests.length === 0) {
      toast.error('Seleziona almeno un ospite');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/events/${eventId}/guests/badges/batch`,
        { guest_ids: selectedGuests },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `badges_${eventId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${selectedGuests.length} badge generati con successo!`);
      setSelectedGuests([]);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Generazione badge disponibile solo nei piani Full ed Enterprise');
      } else {
        toast.error('Errore nella generazione dei badge');
      }
    }
  };

  const toggleGuestSelection = (guestId) => {
    setSelectedGuests(prev => 
      prev.includes(guestId) 
        ? prev.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  const selectAllGuests = () => {
    if (selectedGuests.length === filteredGuests.length) {
      setSelectedGuests([]);
    } else {
      setSelectedGuests(filteredGuests.map(g => g.id));
    }
  };

  const exportPDF = async () => {
    if (!eventData) return;
    
    try {
      const fileName = await generateGuestListPDF(guests, eventData, tenant);
      toast.success(`Guest List PDF generata: ${fileName}`);
    } catch (error) {
      toast.error('Errore durante la generazione PDF');
      console.error(error);
    }
  };

  // Filter and sort guests
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedGuests = guests
    .filter(guest => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        guest.first_name.toLowerCase().includes(query) ||
        guest.last_name.toLowerCase().includes(query) ||
        guest.email.toLowerCase().includes(query) ||
        guest.phone.includes(query)
      );
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      
      let aVal, bVal;
      if (sortField === 'name') {
        aVal = `${a.first_name} ${a.last_name}`;
        bVal = `${b.first_name} ${b.last_name}`;
      } else if (sortField === 'email') {
        aVal = a.email;
        bVal = b.email;
      } else if (sortField === 'payment_status') {
        aVal = a.payment_status || 'free';
        bVal = b.payment_status || 'free';
      } else if (sortField === 'ticket_type') {
        aVal = a.ticket_type?.name || '';
        bVal = b.ticket_type?.name || '';
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });


  return (
    <div className="space-y-6" data-testid="guest-list-manager">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Guest List</h2>
          <p className="text-gray-600 mt-1">Gestisci ospiti e assegna biglietti gratuiti</p>
          {guests.length > 0 && (
            <div className="mt-2 flex gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                {guests.length} {guests.length === 1 ? 'ospite' : 'ospiti'}
              </span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {guests.length > 0 && (
            <>
              <Button variant="outline" onClick={exportContacts} data-testid="export-contacts-button">
                <Download className="w-4 h-4 mr-2" />
                Esporta Contatti
              </Button>
              <Button variant="outline" onClick={exportPDF} data-testid="export-pdf-button">
                <FileText className="w-4 h-4 mr-2" />
                Esporta PDF
              </Button>
              {selectedGuests.length > 0 && (
                <Button 
                  variant="default" 
                  onClick={printBadgesBatch}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Stampa {selectedGuests.length} Badge
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setShowForm(!showForm)} data-testid="add-guest-button">
            <UserPlus className="w-4 h-4 mr-2" />
            Aggiungi Ospite
          </Button>
        </div>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nuovo Ospite - Biglietto Gratuito</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-900">
                  💡 Puoi assegnare biglietti gratuiti o a pagamento (anche se hanno un prezzo). Specifica lo stato di pagamento.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">Nome *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                    data-testid="guest-first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Cognome *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    required
                    data-testid="guest-last-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="guest-email-input"
                />
              </div>

              <div>
                <Label htmlFor="phone">Telefono *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  data-testid="guest-phone-input"
                  placeholder="+39 XXX XXX XXXX"
                />
              </div>

              <div>
                <Label htmlFor="job_title">Qualifica (per badge)</Label>
                <Input
                  id="job_title"
                  value={formData.job_title}
                  onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                  placeholder="es. CEO, Developer, Manager..."
                  data-testid="guest-job-title-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La qualifica apparirà sui badge stampati (piani Full/Enterprise)
                </p>
              </div>

              <div>
                <Label>Tipologia Biglietto *</Label>
                <Select 
                  value={formData.ticket_type_id} 
                  onValueChange={(value) => setFormData({...formData, ticket_type_id: value})}
                  required
                >
                  <SelectTrigger data-testid="guest-ticket-type-select">
                    <SelectValue placeholder="Seleziona tipologia" />
                  </SelectTrigger>
                  <SelectContent>
                    {ticketTypes.map(tt => (
                      <SelectItem key={tt.id} value={tt.id}>
                        {tt.name} {tt.price > 0 && `(€${tt.price.toFixed(2)})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Stato Pagamento *</Label>
                <Select 
                  value={formData.payment_status} 
                  onValueChange={(value) => setFormData({...formData, payment_status: value})}
                  required
                >
                  <SelectTrigger data-testid="guest-payment-status-select">
                    <SelectValue placeholder="Seleziona stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Gratuito</SelectItem>
                    <SelectItem value="paid">Pagato</SelectItem>
                    <SelectItem value="pending">In Attesa di Pagamento</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Indica se il biglietto è gratuito, già pagato o in attesa di pagamento
                </p>
              </div>

              <div>
                <Label htmlFor="notes">Note</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  data-testid="guest-notes-input"
                  placeholder="Note interne sull ospite..."
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" data-testid="save-guest-button">Aggiungi Ospite</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {guests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserPlus className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun ospite ancora</h3>
            <p className="text-gray-600 mb-6">Aggiungi ospiti alla guest list e assegna loro biglietti gratuiti</p>
            <Button onClick={() => setShowForm(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Aggiungi Primo Ospite
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Search bar */}
          <div className="flex items-center gap-2 bg-white p-4 rounded-lg border border-gray-200">
            <Search className="w-5 h-5 text-gray-400" />
            <Input
              placeholder="Cerca per nome, email o telefono..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 border-0 focus-visible:ring-0"
            />
            {searchQuery && (
              <Button variant="ghost" size="sm" onClick={() => setSearchQuery('')}>
                Cancella
              </Button>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedGuests.length === filteredAndSortedGuests.length && filteredAndSortedGuests.length > 0}
                          onChange={selectAllGuests}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center gap-1">
                          Ospite
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contatti</th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('ticket_type')}
                      >
                        <div className="flex items-center gap-1">
                          Tipologia
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('payment_status')}
                      >
                        <div className="flex items-center gap-1">
                          Stato Pagamento
                          <ArrowUpDown className="w-3 h-3" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAndSortedGuests.map(guest => (
                    <tr key={guest.id} className="hover:bg-gray-50" data-testid={`guest-row-${guest.id}`}>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedGuests.includes(guest.id)}
                          onChange={() => toggleGuestSelection(guest.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{guest.first_name} {guest.last_name}</div>
                        {guest.job_title && (
                          <div className="text-xs text-gray-500 mt-1">{guest.job_title}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="flex items-center gap-1 mb-1">
                          <Mail className="w-3 h-3" />
                          {guest.email}
                        </div>
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {guest.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {guest.ticket_type?.name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          guest.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                          guest.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {guest.payment_status === 'paid' ? 'Pagato' :
                           guest.payment_status === 'pending' ? 'In Attesa' :
                           'Gratuito'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{guest.notes || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => viewTicket(guest.id)}
                            data-testid={`view-ticket-${guest.id}`}
                            title="Visualizza biglietto"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => resendTicket(guest.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            data-testid={`resend-ticket-${guest.id}`}
                            title="Reinvia biglietto via email"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => printBadge(guest.id)}
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            title="Stampa badge (Full/Enterprise)"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(guest.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-guest-${guest.id}`}
                            title="Elimina ospite"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredAndSortedGuests.length === 0 && searchQuery && (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <Search className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-gray-600">Nessun ospite trovato per "{searchQuery}"</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      <Dialog open={showTicket} onOpenChange={setShowTicket}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Biglietto Ospite</DialogTitle>
          </DialogHeader>
          {guestTicket && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Intestatario</p>
                <p className="text-lg font-semibold">{guestTicket.holder_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Email</p>
                <p>{guestTicket.holder_email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Tipologia</p>
                <p>{guestTicket.ticket_type?.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Zone di accesso</p>
                <div className="space-y-1 mt-1">
                  {guestTicket.zones?.map(zone => (
                    <div key={zone.id} className="text-sm text-gray-600">• {zone.name}</div>
                  ))}
                </div>
              </div>
              <div className="bg-white border-2 border-dashed border-gray-300 p-6 rounded-lg text-center">
                <p className="text-sm font-medium text-gray-700 mb-4">QR Code Biglietto</p>
                {qrCodeUrl ? (
                  <div className="flex flex-col items-center gap-3">
                    <img 
                      src={qrCodeUrl} 
                      alt="QR Code Biglietto" 
                      className="w-64 h-64 border-4 border-gray-200 rounded-lg shadow-md"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Scansiona questo codice per il check-in
                    </p>
                    <code className="text-xs bg-gray-100 px-3 py-1 rounded text-gray-600">
                      {guestTicket.qr_code}
                    </code>
                  </div>
                ) : (
                  <div className="text-gray-400">Generazione QR code...</div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
